import React from 'react';
import { ButtonPrimary, ButtonSecondary } from '@components/button';
import classNames from 'classnames';

import { ModalFooterProps } from './modal-footer.types';

/**
 * ModalFooter component - New footer design based on Figma specs
 *
 * Responsive design:
 * - Mobile: 16px padding, full-width buttons, extra 16px bottom padding
 * - Desktop (sm+): 32px/24px padding, buttons side by side (auto width)
 *
 * Key features:
 * - Primary and secondary buttons only (per Figma design)
 * - Sticky positioning
 * - Border top
 * - Form support (submit button)
 *
 * @example
 * ```tsx
 * <ModalFooter
 *   primaryButtonText="Apply"
 *   secondaryButtonText="Clear filters"
 *   onPrimaryClick={handleApply}
 *   onSecondaryClick={handleClear}
 * />
 * ```
 */
export const ModalFooter = ({
  primaryButtonText,
  secondaryButtonText,
  onPrimaryClick,
  onSecondaryClick,
  isSubmit = false,
  isPrimaryDisabled = false,
  isSecondaryDisabled = false,
  primaryDataTestId,
  secondaryDataTestId,
  isPrimaryLoading = false,
}: ModalFooterProps) => {
  // Don't render if no buttons are provided
  if (!primaryButtonText && !secondaryButtonText) {
    return null;
  }

  return (
    <footer
      className={classNames(
        'sticky bottom-0 w-full z-20 bg-primary border-t border-extra-soft',
        'p-4 pb-8 sm:px-8 sm:py-6'
      )}>
      <div className='flex gap-2 items-center justify-end w-full max-w-[1200px]'>
        {/* Mobile: Full width buttons */}
        {/* Desktop: Auto width buttons */}
        {secondaryButtonText && (
          <ButtonSecondary
            onClick={onSecondaryClick}
            disabled={isSecondaryDisabled}
            data-testid={secondaryDataTestId}
            className='flex-1 sm:flex-none'>
            {secondaryButtonText}
          </ButtonSecondary>
        )}

        {primaryButtonText && (
          <ButtonPrimary
            type={isSubmit ? 'submit' : 'button'}
            onClick={onPrimaryClick}
            disabled={isPrimaryDisabled || isPrimaryLoading}
            data-testid={primaryDataTestId}
            className='flex-1 sm:flex-none'>
            {primaryButtonText}
          </ButtonPrimary>
        )}
      </div>
    </footer>
  );
};
